// FieldVision - Dashboard.tsx - v3.2
// RESTRUCTURE 2026-01-14: 3 sektioner med flex layout
// - Sektion 1: Markoversigt (fast)
// - Sektion 2: Tildelingskort (fleksibel) med info-modal og seneste bestillinger
// - Sektion 3: Logfiler/As-applied (fast)
// TASK 2: Scroll + TYPE filter på bestillinger-tabel
// TASK 3: Upload modal med ApplicationLogUploadModal + success dialog

import React, { useState, useEffect, useMemo } from 'react';
import MarkerMap from './MarkerMap';
import PricingModal from './PricingModal';
import ApplicationLogUploadModal from './ApplicationLogUploadModal';
import apiService from '../services/api';

const SERVICE_TYPE_LABELS: Record<string, string> = {
  'NDVI': 'NDVI',
  'NDRE': 'NDRE',
  'WEED': 'Ukrudt',
  'REGROWTH': 'Genvækst',
  'SPECIAL': 'Special'
};

interface Order {
  orderId: number;
  orderNumber: string;
  createdAt: string;
  serviceType: string;
  source: string;
  status: string;
  fieldCount: number;
  fieldNames: string[];
}

interface DashboardProps {
  user: {
    userId: number;
    email: string;
    name: string;
  };
  onNavigate: (view: 'myFields' | 'booking' | 'mineMarkerOversigt' | 'mineLogfilerOversigt') => void;
  onNavigateToFieldDetail?: (fieldId: number, fieldData?: any) => void;
  currentCVR: string;
  selectedFieldIds?: string[];
  zoomLoadedFields?: any[];
}

// Helper function to format field names for display
const formatFieldNames = (fieldNames: string[], maxDisplay: number = 3): string => {
  if (!fieldNames || fieldNames.length === 0) {
    return 'Ingen marker';
  }

  const formattedNames = fieldNames.map(name => {
    // Fjernet "Mark " prefix - landmanden ved det er marker
    const trimmed = name.trim();
    return trimmed;
  });

  if (formattedNames.length <= maxDisplay) {
    return formattedNames.join(', ');
  }

  const displayed = formattedNames.slice(0, maxDisplay);
  const remaining = formattedNames.length - maxDisplay;
  return `${displayed.join(', ')} +${remaining} mere`;
};

const Dashboard: React.FC<DashboardProps> = ({
  user,
  onNavigate,
  onNavigateToFieldDetail,
  currentCVR,
  selectedFieldIds = [],
  zoomLoadedFields = []
}) => {
  const [geojson, setGeojson] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | 'satellite' | 'drone'>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadSuccessDialog, setShowUploadSuccessDialog] = useState(false);
  const [showLogInfoModal, setShowLogInfoModal] = useState(false);

  // Konverter geojson til fields format for MarkerMap
  const dashboardFields = useMemo(() => {
    if (!geojson?.features) return [];

    // CVR marker (filtreret til valgte)
    const cvrFields = geojson.features
      .filter((f: any) => selectedFieldIds.includes(`${f.properties.CVR}-${f.properties.Marknr}`))
      .map((f: any) => ({
        Id: f.properties.Id,
        Marknr: f.properties.Marknr,
        CVR: f.properties.CVR,
        IMK_areal: f.properties.IMK_areal,
        Geometry: JSON.stringify(f.geometry),
        isZoomLoaded: false
      }));

    // Tilfoej zoomLoadedFields hvis de findes - konverter til Field format
    const zoomFields = (zoomLoadedFields || []).map((f: any) => {
      const props = f.properties || f;
      return {
        Id: props.Id || props.id || Math.floor(Math.random() * 100000),
        Marknr: props.Marknr || props.marknr || '',
        CVR: props.CVR || props.cvr || '',
        IMK_areal: props.IMK_areal || props.GEOMETRISK || props.areal || 0,
        Geometry: f.geometry ? JSON.stringify(f.geometry) : props.Geometry,
        isZoomLoaded: true
      };
    });

    return [...cvrFields, ...zoomFields];
  }, [geojson, selectedFieldIds, zoomLoadedFields]);

  // Filter orders baseret på typeFilter
  const filteredOrders = useMemo(() => {
    if (typeFilter === 'all') return orders;
    return orders.filter(order =>
      typeFilter === 'satellite'
        ? order.source === 'satellite'
        : order.source !== 'satellite'
    );
  }, [orders, typeFilter]);

  // Handle TYPE filter cycle
  const handleTypeFilterClick = () => {
    setTypeFilter(current => {
      if (current === 'all') return 'satellite';
      if (current === 'satellite') return 'drone';
      return 'all';
    });
  };

  // Handle klik på mark i kortet - navigér til mark detaljer
  const handleFieldClick = (fieldKey: string) => {
    const field = dashboardFields.find((f: any) => `${f.CVR}-${f.Marknr}` === fieldKey);
    if (field && onNavigateToFieldDetail) {
      const fieldId = field.Id || 0;
      const navigationData = {
        marknr: field.Marknr,
        cvr: field.CVR,
        areal: field.IMK_areal,
        id: fieldId,
        geometry: field.Geometry
      };
      onNavigateToFieldDetail(fieldId, navigationData);
    }
  };

  // State for hover
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);

  useEffect(() => {
    loadRecentOrders();

    if (currentCVR && currentCVR.trim() !== '') {
      loadUserFields();
    } else {
      setGeojson(null);
    }
  }, [currentCVR]);

  const loadUserFields = async () => {
    try {
      setLoading(true);
      const cvrsToLoad = currentCVR.split(',').map(cvr => cvr.trim());
      console.log('Dashboard loading fields for selected CVRs:', cvrsToLoad);

      const fieldsData = await apiService.searchFields(cvrsToLoad);
      setGeojson(fieldsData);
    } catch (error) {
      console.error('Error loading fields:', error);
      setGeojson(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentOrders = async () => {
    try {
      const data = await apiService.getOrders();

      const formattedOrders = data.orders.map((order: any) => ({
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        createdAt: new Date(order.createdAt).toLocaleDateString('da-DK'),
        serviceType: order.serviceType,
        source: order.source || 'web',
        status: order.status === 'pending' ? 'Behandles' : order.status,
        fieldCount: order.fieldCount,
        fieldNames: order.fieldNames || []
      }));

      setOrders(formattedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Left Sidebar - 3 sektioner med flex layout */}
      <div className="dashboard-sidebar">

        {/* Sektion 1: Markoversigt (FAST) */}
        <div className="section-box section-fixed">
          <h2>Markoversigt</h2>
          <p className="section-description">
            Få overblik over dine marker. Se bestilte kort og logfiler på dine marker.
          </p>
          <button
            className="btn-primary"
            onClick={() => onNavigate('mineMarkerOversigt')}
          >
            Vis marker
          </button>
        </div>

        {/* Sektion 2: Tildelingskort (FLEKSIBEL) */}
        <div className="section-box section-flexible">
          <div className="section-header-with-icon">
            <h2>Tildelingskort</h2>
            <span
              className="info-icon-small"
              onClick={() => setShowInfoModal(true)}
              title="Hvad er tildelingskort?"
            >
              &#9432;
            </span>
          </div>
          <p className="section-description">
            Bestil ukrudtskort og tildelingskort (drone eller satellit), der passer til din terminal.{' '}
            <a
              href="#"
              className="link-inline"
              onClick={(e) => {
                e.preventDefault();
                setShowPricingModal(true);
              }}
            >
              Se priser
            </a>
          </p>
          <button
            className="btn-primary"
            onClick={() => onNavigate('booking')}
          >
            Bestil kort
          </button>

          {/* Seneste bestillinger - med scroll og TYPE filter */}
          <div className="orders-section">
            <h3>Seneste bestillinger</h3>
            <div className="orders-table-container">
              <div className="orders-table">
                <table>
                  <thead>
                    <tr>
                      <th>DATO</th>
                      <th>MARK</th>
                      <th
                        onClick={handleTypeFilterClick}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Klik for at filtrere"
                      >
                        TYPE {typeFilter === 'satellite' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="#16a34a" style={{verticalAlign: 'middle', marginLeft: '4px'}}><path d="M560-32v-80q117 0 198.5-81.5T840-392h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T560-32Zm0-160v-80q50 0 85-35t35-85h80q0 83-58.5 141.5T560-192ZM222-57q-15 0-30-6t-27-17L23-222q-11-12-17-27t-6-30q0-16 6-30.5T23-335l127-127q23-23 57-23.5t57 22.5l50 50 28-28-50-50q-23-23-23-56t23-56l57-57q23-23 56.5-23t56.5 23l50 50 28-28-50-50q-23-23-23-56.5t23-56.5l127-127q12-12 27-18t30-6q15 0 29.5 6t26.5 18l142 142q12 11 17.5 25.5T895-730q0 15-5.5 30T872-673L745-546q-23 23-56.5 23T632-546l-50-50-28 28 50 50q23 23 22.5 56.5T603-405l-56 56q-23 23-56.5 23T434-349l-50-50-28 28 50 50q23 23 22.5 57T405-207L278-80q-11 11-25.5 17T222-57Zm0-79 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142Zm184-184 56-56-142-142-56 56 142 142Zm198-198 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142ZM448-504Z"/></svg>
                        ) : typeFilter === 'drone' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="#16a34a" style={{verticalAlign: 'middle', marginLeft: '4px'}}><path d="M160-160q0-75 41-134.5T307-381l-10-99H160v-240H40v-80h320v80H240v160h48l-8-80h400l-8 80h48v-160H600v-80h321v80H801v240H664l-10 99q65 27 105.5 86.5T800-160h-80q0-66-47-113t-113-47H400q-66 0-113 47t-47 113h-80Zm225-240h189l17-160H368l17 160Zm0 0h189-189Z"/></svg>
                        ) : null}
                        <span style={{ fontSize: '10px', marginLeft: '4px', color: '#9ca3af' }}>▼</span>
                      </th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                          {orders.length === 0 ? 'Ingen bestillinger endnu' : 'Ingen bestillinger matcher filteret'}
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => (
                        <tr
                          key={order.orderId}
                          onClick={() => onNavigateToFieldDetail && onNavigateToFieldDetail(order.orderId, order)}
                          style={{ cursor: 'pointer' }}
                          className="order-row"
                        >
                          <td>{order.createdAt}</td>
                          <td title={order.fieldNames.join(', ')}>
                            {formatFieldNames(order.fieldNames, 2)}
                          </td>
                          <td>{order.source === 'satellite' ? (
                            <><svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#333333" style={{verticalAlign: 'middle', marginRight: '4px'}}><path d="M560-32v-80q117 0 198.5-81.5T840-392h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T560-32Zm0-160v-80q50 0 85-35t35-85h80q0 83-58.5 141.5T560-192ZM222-57q-15 0-30-6t-27-17L23-222q-11-12-17-27t-6-30q0-16 6-30.5T23-335l127-127q23-23 57-23.5t57 22.5l50 50 28-28-50-50q-23-23-23-56t23-56l57-57q23-23 56.5-23t56.5 23l50 50 28-28-50-50q-23-23-23-56.5t23-56.5l127-127q12-12 27-18t30-6q15 0 29.5 6t26.5 18l142 142q12 11 17.5 25.5T895-730q0 15-5.5 30T872-673L745-546q-23 23-56.5 23T632-546l-50-50-28 28 50 50q23 23 22.5 56.5T603-405l-56 56q-23 23-56.5 23T434-349l-50-50-28 28 50 50q23 23 22.5 57T405-207L278-80q-11 11-25.5 17T222-57Zm0-79 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142Zm184-184 56-56-142-142-56 56 142 142Zm198-198 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142ZM448-504Z"/></svg>{SERVICE_TYPE_LABELS[order.serviceType] || order.serviceType}</>
                          ) : (
                            <><svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#333333" style={{verticalAlign: 'middle', marginRight: '4px'}}><path d="M160-160q0-75 41-134.5T307-381l-10-99H160v-240H40v-80h320v80H240v160h48l-8-80h400l-8 80h48v-160H600v-80h321v80H801v240H664l-10 99q65 27 105.5 86.5T800-160h-80q0-66-47-113t-113-47H400q-66 0-113 47t-47 113h-80Zm225-240h189l17-160H368l17 160Zm0 0h189-189Z"/></svg>{SERVICE_TYPE_LABELS[order.serviceType] || order.serviceType}</>
                          )}</td>
                          <td>
                          <div>{order.status}</div>
                          <div style={{ color: '#6b7280' }}>#{order.orderId}</div>
                        </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Sektion 3: Logfiler/As-applied (FAST) */}
        <div className="section-box section-fixed">
          <div className="section-header-with-icon">
            <h2>Logfiler (As-applied)</h2>
            <span 
              className="info-icon-small" 
              onClick={() => setShowLogInfoModal(true)}
              title="Hvad er logfiler?"
            >&#9432;</span>
          </div>
          <p className="section-description">
            Upload as-applied filer for at dokumentere den faktiske tildeling pr. mark. Én fil kan indeholde flere marker.
          </p>
          <button
            className="btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            Upload logfiler
          </button>
        </div>

      </div>

      {/* Right Side - Map */}
      <div className="dashboard-map">
        {loading ? (
          <div className="loading-message">
            <p>Indlæser dine marker...</p>
          </div>
        ) : dashboardFields.length > 0 ? (
          <MarkerMap
            fields={dashboardFields}
            onFieldClick={handleFieldClick}
            selectedMarknr={null}
            onFieldHover={setHoveredFieldId}
          />
        ) : (
          <div className="empty-map-message">
            <p>{currentCVR ? 'Ingen marker fundet' : 'Vaelg dine marker for at se dem på kortet'}</p>
            <button
              className="btn-primary"
              onClick={() => onNavigate('mineMarkerOversigt')}
            >
              Vis marker
            </button>
          </div>
        )}
      </div>

      {/* Info Modal - indhold fra gamle info-box */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h2>Hvad er tildelingskort og ukrudtskort?</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowInfoModal(false)}
              >
                &#10005;
              </button>
            </div>
            <div className="info-modal-content">
              <h3>Tildelingskort</h3>
              <p>
                Dansk Planteinspektion leverer tildelingskort til landbruget bygget på enten
                drone- eller satellitdata. Tildelingskortet er bygget på en fotosynteseanalyse
                af afgrøden og bruges efterfølgende til at tildele planteværn og kvælstof præcist
                der i afgrøden, hvor det kan gøre en forskel.
              </p>

              <h3>Ukrudtskort</h3>
              <p>
                Dansk Planteinspektion leverer ukrudtskort til landbruget bygget på dronebilleder,
                der kan bruges til at tildele planteværn præcist der, hvor det kan gøre en forskel.
              </p>
              <ul>
                <li>Spar op til 80% af planteværn.</li>
                <li>Minimer brugen af planteværn ved at bekæmpe præcist der, hvor der er uønsket vækst.</li>
                <li>Vi leverer bl.a. ukrudtskort, der fanger tidsler i korn og ukrudt i frøgræs.</li>
                <li>Med et nedvisningskort til kartofler behøver du kun at give anden og tredje behandling der, hvor der er behov for det</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal - genbruger eksisterende komponent */}
      <ApplicationLogUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        cvr={currentCVR}
        marknr=""
        onUploadSuccess={() => {
          setShowUploadModal(false);
          setShowUploadSuccessDialog(true);
        }}
      />

      {/* Upload Success Dialog */}
      {showUploadSuccessDialog && (
        <div className="modal-overlay" onClick={() => setShowUploadSuccessDialog(false)}>
          <div className="success-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="success-dialog-icon">&#10003;</div>
            <h2>Upload gennemført!</h2>
            <p>Logfilen er gemt og matchet med dine marker.</p>
            <div className="success-dialog-buttons">
              <button
                className="btn-secondary"
                onClick={() => setShowUploadSuccessDialog(false)}
              >
                Til forsiden
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowUploadSuccessDialog(false);
                  onNavigate('mineMarkerOversigt');
                }}
              >
                Markoversigt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Info Modal */}
      {showLogInfoModal && (
        <div className="modal-overlay" onClick={() => setShowLogInfoModal(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h2>Hvad er tildelingslogfiler?</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setShowLogInfoModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="info-modal-content">
              <p>
                Tildelingslogfiler (as-applied) dokumenterer den faktiske mængde 
                gødning eller planteværn, der er spredt på dine marker. Filerne 
                genereres automatisk af din traktor eller sprøjte under arbejdet.
              </p>

              <h3>Sådan virker det</h3>
              <ul>
                <li>Upload én .zip fil fra din terminal (ISOXML eller shapefile)</li>
                <li>Systemet analyserer GPS-koordinaterne i filen</li>
                <li>Logfilerne matches automatisk til de rigtige marker</li>
                <li>Du kan se den faktiske tildeling visualiseret som et zone-kort</li>
              </ul>

              <h3>Understøttede formater</h3>
              <ul>
                <li>ISOXML (.zip) - de fleste terminaler</li>
                <li>Shapefile (.zip) - eksport fra terminaler</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />

      <style>{`
        .dashboard-container {
          display: grid;
          grid-template-columns: 400px 1fr;
          height: calc(100vh - 80px);
          gap: 0;
        }

        .dashboard-sidebar {
          background-color: #f5f5f5;
          padding: 0;
          overflow-y: auto;
          border-right: 1px solid #ddd;
          display: flex;
          flex-direction: column;
          height: calc(100vh - 80px);
        }

        .dashboard-map {
          position: relative;
          height: 100%;
        }

        /* Fast sektion (top og bund) */
        .section-fixed {
          flex-shrink: 0;
          padding: 20px;
          margin: 12px;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        /* Fleksibel sektion (midten) */
        .section-flexible {
          flex: 1;
          padding: 20px;
          margin: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          min-height: 0;
        }

        /* Header med info-ikon */
        .section-header-with-icon {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }

        .section-header-with-icon h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
        }

        /* Info-ikon (lille) */
        .info-icon-small {
          color: #3b82f6;
          font-size: 16px;
          cursor: pointer;
        }

        .info-icon-small:hover {
          opacity: 0.7;
        }

        /* Inline link i tekst */
        .link-inline {
          color: #16a34a;
          text-decoration: none;
        }

        .link-inline:hover {
          text-decoration: underline;
        }

        /* Orders section inden i Tildelingskort */
        .orders-section {
          margin-top: 20px;
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .orders-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
          flex-shrink: 0;
        }

        .orders-table-container {
          flex: 1;
          overflow-y: auto;
          min-height: 150px;
        }

        /* Scrollbar styling */
        .orders-table-container::-webkit-scrollbar {
          width: 6px;
        }

        .orders-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .orders-table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .orders-table-container::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        /* Info Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .info-modal {
          background-color: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        }

        .info-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .info-modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .modal-close-btn {
          background: none;
          border: none;
          font-size: 20px;
          color: #6b7280;
          cursor: pointer;
          padding: 4px 8px;
        }

        .modal-close-btn:hover {
          color: #374151;
        }

        .info-modal-content {
          padding: 24px;
          font-size: 14px;
          line-height: 1.6;
          color: #4b5563;
        }

        .info-modal-content h3 {
          margin-top: 20px;
          margin-bottom: 10px;
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .info-modal-content h3:first-child {
          margin-top: 0;
        }

        .info-modal-content ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .info-modal-content li {
          margin: 8px 0;
        }

        .section-box h2 {
          font-size: 18px;
          margin-bottom: 4px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .section-description {
          font-size: 14px;
          color: #666;
          margin-bottom: 15px;
          line-height: 1.5;
        }

        .orders-table {
          overflow-x: auto;
        }

        .orders-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        /* Sticky header */
        .orders-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .orders-table th {
          background-color: #E8F5E9;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          color: #2E7D32;
          font-size: 12px;
        }

        .orders-table td {
          padding: 12px 10px;
          border-bottom: 1px solid #eee;
          color: #666;
        }

        .orders-table tbody tr:hover {
          background-color: #f5f5f5;
        }

        .loading-message,
        .empty-map-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #666;
        }

        .empty-map-message button {
          margin-top: 20px;
        }

        /* Upload Success Dialog */
        .success-dialog {
          background-color: white;
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          padding: 32px 24px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        }

        .success-dialog-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background-color: #dcfce7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          color: #16a34a;
        }

        .success-dialog h2 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .success-dialog p {
          margin: 0 0 24px 0;
          font-size: 14px;
          color: #6b7280;
        }

        .success-dialog-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .btn-secondary {
          padding: 10px 20px;
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          color: #374151;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-secondary:hover {
          background-color: #f3f4f6;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
